import { IAuthToken } from '@qima-labs/app/shared/models/interfaces/auth-token.interface';
import { AI_API_ACCESS_TOKEN } from '@qsp-ui-library/constants/ai-api-access-token.constants';
import { EStorageKeys } from '@qsp-ui-library/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@qsp-ui-library/models/interfaces/http-response-body.interface';
import { AppAuthenticationService } from '@qsp-ui-library/services/user-services/app-authentication/app-authentication.service';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  public constructor(
    private readonly _appAuthenticationService: AppAuthenticationService,
    private readonly _cookieStorage: CookieService
  ) {}

  public intercept<T>(request: Readonly<HttpRequest<T>>, next: Readonly<HttpHandler>): Observable<HttpEvent<T>> {
    const token = this._cookieStorage.get(EStorageKeys.AUTH_TOKEN);
    let userId = this._cookieStorage.get(EStorageKeys.USER_ID);
    const refreshTokenExpire = this._cookieStorage.get(EStorageKeys.REFRESH_TOKEN_EXPIRE);
    const authTokenExpire = this._cookieStorage.get(EStorageKeys.AUTH_TOKEN_EXPIRE);
    const shouldRefresh =
      refreshTokenExpire &&
      authTokenExpire &&
      token &&
      !request.url.includes('/v1.0/security/azure/token?refreshToken') &&
      this._isTokenExpired(EStorageKeys.AUTH_TOKEN_EXPIRE) &&
      !this._isTokenExpired(EStorageKeys.REFRESH_TOKEN_EXPIRE);

    if (shouldRefresh) {
      return this._appAuthenticationService.refreshAccessToken$().pipe(
        catchError((_error): Observable<IHttpResponseBody<IAuthToken> | null> => {
          // refresh token failed, navigate to login
          this._appAuthenticationService.navigateToLogin();

          return of();
        }),
        switchMap((response: IHttpResponseBody<IAuthToken> | null): Observable<HttpEvent<T>> => {
          // If refresh token failed (response is null), return empty observable
          if (!response) {
            return of();
          }

          if (response?.content) {
            this._appAuthenticationService.saveTokenAndExpiration(response.content);
          }

          const newToken = this._cookieStorage.get(EStorageKeys.AUTH_TOKEN);

          userId = this._cookieStorage.get(EStorageKeys.USER_ID);

          if (newToken && userId) {
            const newRequest = request.clone({
              setHeaders: {
                'authorization': `Bearer ${newToken}`,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'ai-user-id': userId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'ai-api-access-token': AI_API_ACCESS_TOKEN,
              },
            });

            return next.handle(newRequest);
          }

          // else ,not exist newToken or userId ,navigate to login
          this._appAuthenticationService.navigateToLogin();

          return of();
        })
      );
    }

    if (token && userId) {
      request = request.clone({
        setHeaders: {
          'authorization': `Bearer ${token}`,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'ai-user-id': userId,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'ai-api-access-token': AI_API_ACCESS_TOKEN,
        },
      });
    } else {
      this._appAuthenticationService.navigateToLogin();
    }

    return next.handle(request);
  }

  private _isTokenExpired(tokenKey: EStorageKeys): boolean {
    const tokenExpiredTime = this._cookieStorage.get(tokenKey) || 0;

    return Date.now() / 1000 >= +tokenExpiredTime;
  }
}
